import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

type ScanInterceptor = (code: string) => boolean;

type ScannerContextType = {
  active: boolean;
  setActive: (active: boolean) => void;
  registerInterceptor: (fn: ScanInterceptor) => () => void;
  runInterceptors: (code: string) => boolean;
};

const ScannerContext = createContext<ScannerContextType>({
  active: false,
  setActive: () => {},
  registerInterceptor: () => () => {},
  runInterceptors: () => false,
});

export function useScannerContext() {
  return useContext(ScannerContext);
}

export function ScannerProvider({ children }: PropsWithChildren) {
  const [active, setActive] = useState(false);
  const interceptorsRef = useRef<Set<ScanInterceptor>>(new Set());

  const registerInterceptor = useCallback((fn: ScanInterceptor) => {
    interceptorsRef.current.add(fn);
    return () => {
      interceptorsRef.current.delete(fn);
    };
  }, []);

  const runInterceptors = useCallback((code: string) => {
    for (const fn of interceptorsRef.current) {
      if (fn(code)) return true;
    }
    return false;
  }, []);

  return (
    <ScannerContext.Provider
      value={{ active, setActive, registerInterceptor, runInterceptors }}
    >
      {children}
    </ScannerContext.Provider>
  );
}
